import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { Spin, Space, Form, Button, Row, Col, Select, Input, Typography, Switch } from 'antd';
import { addToast } from "../../features/toast/toastsSlice"

import {
	SearchOutlined,
	UploadOutlined,
	ContainerOutlined,
	PlusCircleOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { setPageTitle } from '../../features/header/headerSlice.js';
import styles from './VehicleCompatibilitySearch.module.scss';
import logo from "../../assets/css/default/image/logo_color.svg";
import VehicleCompatibilitySearchModal from './VehicleCompatibilitySearchModal.js';
import { useGetVehicleModelsQuery } from '../../features/vehicleModels/vehicleModelApi.js';
import { useCurrentCompany } from '../../features/company/companySlice.js';
import { VehicleMakeModelYearFormModal } from './VehicleMakeModelYearFormModal.js';
import { useGetRegionsQuery } from '../../features/regions/regionsApi.js';
import { useUserInfo } from '../../features/user/userApi.js';
import Multiselect from '../../features/multiselect/Multiselect.js';
import { getMMYCombination } from './VehicleModelHelper.js';
import { useMediaQuery } from 'react-responsive';
import { RouteKey } from '../../config/page-key.js';
import { Can } from '../../features/user/Can.js';
import { FeatureFlag, Permission } from '../../features/user/Permission.js';
import { ResponsiveButton } from '../../features/responsive/ResponsiveButton.js';
import { VehicleGenericSearchFormModal } from './VehicleGenericSearchFormModal.js';
import { setGenericSearchEnabled } from '../../features/vehicleModels/vehicleModelFilterSlice.js';
const { Text, Title } = Typography;

const VehicleCompatibilitySearch = () => {
	const history = useHistory();
	const dispatch = useDispatch();
	const { i18n, t } = useTranslation();
	const [searchForm] = Form.useForm();
	const currentCompany = useCurrentCompany();
	const { data: vehicleModels, isFetching: isVehicleCompatibilityLoading } = useGetVehicleModelsQuery({ companyId: currentCompany?.id }, { pollingInterval: 3600000, skip: currentCompany?.id === undefined });
	const [showCsvSearch, setShowCsvSearch] = useState(false);
	const [newVehicleModelModal, setNewVehicleModelModal] = useState(false);
	const data = useUserInfo();
	const { data: regions } = useGetRegionsQuery(undefined, { skip: data.id === undefined });
	const company = useCurrentCompany();
	const formValues = Form.useWatch([], searchForm);
	const isMobile = useMediaQuery({ maxWidth: 767 });
	const useGenericSearch = useSelector(state => state.vehicleModelFilter.genericSearchEnabled);

	const fullValues = useMemo(() => {
		return getMMYCombination(formValues, vehicleModels);
	}, [vehicleModels, formValues]);

	const feedbackUrl = useMemo(() => {
		let link;

		if (regions == null || company == null) {
			return link;
		}
		const region = regions.find(r => r.code === company.country);

		if (region == null) {
			return link;
		}

		try {
			const configJson = JSON.parse(region.config);
			if (configJson.vlt_feedback_url) {
				link = configJson.vlt_feedback_url;
			}
		}
		catch {
			console.error('Region has no config or config json has format problem.');
		}
		return link;
	}, [company, regions]);

	useEffect(() => {
		dispatch(setPageTitle(t("VehicleModel.VehicleLookupTool")));
	}, [dispatch]);

	const onFinish = (e) => {
		history.push(RouteKey.VEHICLE_COMPATIBILITY, (!!e.make || !!e.model || !!e.year || !!e.q) ? e : undefined)
	};

	return (
		<Spin spinning={isVehicleCompatibilityLoading}>
			<Space direction={"vertical"} className={styles.mainPanel} size={40}>
				<div className={styles.LogoWordmark}>
					<img src={logo} />
				</div>
				<Title level={4} className={styles.centerRow}>{t("VehicleModel.VehicleSearchPlaceHolder")}</Title>
				<Form form={searchForm} layout="inline" onFinish={onFinish} className={styles.searchForm}>
					{useGenericSearch ?
						<Row justify="center" className={styles.fullWidthRow}>
							<Col xs={{ span: 18 }} xl={{ span: 12, offset: 0 }} className={"mb-2"}>
								<Form.Item name="q" 
								  style={{ ...(isMobile && { margin: 0 }) }}
								  rules={[
									{ required: true, message: t("ValidationErrors.KeywordRequired") },
									{
										pattern: /^[^&$!:]*$/,
										message: t("ValidationErrors.InvalidKeyword"),
									}
								]}>
									<Input size="large" style={{ width: "100%" }} placeholder={t('VehicleModel.VehicleSearchPlaceHolder')} />
								</Form.Item>
							</Col>
							<Col xs={{ span: 6 }} lg={{ span: 6 }} xl={{ span: 4 }} className={["mb-2", isMobile ? styles.centerRow : undefined]}>
								<Button
									type="primary"
									size="large"
									style={{ width: "75%" }}
									htmlType="submit"
									disabled={!formValues?.q}
								>
									{t('Common.Search')} <SearchOutlined />
								</Button>
							</Col>
						</Row>
						: <Row justify="center" className={styles.fullWidthRow}>
							<Col xs={{ span: 24 }} lg={{ span: 6 }} xl={{ span: 4 }} className={"mb-2"}>
								<Form.Item name="make" style={{ ...(isMobile && { margin: 0 }) }}>
									<Multiselect
										options={fullValues.make}
										placeholder={t("VehicleModel.Make")}
										showBadge={false}
										maxCount={15}
										size="large"
										isLoading={isVehicleCompatibilityLoading} />
								</Form.Item>
							</Col>
							<Col xs={{ span: 24 }} lg={{ span: 6 }} xl={{ span: 4 }} className={"mb-2"}>
								<Form.Item name="model" style={{ ...(isMobile && { margin: 0 }) }}>
									<Multiselect
										options={fullValues.model}
										showBadge={false}
										maxCount={15}
										size="large"
										placeholder={t("VehicleModel.Model")}
										isLoading={isVehicleCompatibilityLoading} />
								</Form.Item>
							</Col>
							<Col xs={{ span: 24 }} lg={{ span: 6 }} xl={{ span: 4 }} className={"mb-2"}>
								<Form.Item name="year" style={{ ...(isMobile && { margin: 0 }) }}>
									<Multiselect
										options={fullValues.year}
										showBadge={false}
										size="large"
										placeholder={t("VehicleModel.Year")}
										isLoading={isVehicleCompatibilityLoading} />
								</Form.Item>
							</Col>
							<Col xs={{ span: 24 }} lg={{ span: 6 }} xl={{ span: 4 }} className={["mb-2", isMobile ? styles.centerRow : undefined]}>
								<Button
									type="primary"
									size="large"
									style={{ width: "75%" }}
									htmlType="submit"
									disabled={(formValues?.make || []).length === 0 || (formValues?.model || []).length === 0}
								>
									{t('Common.Search')} <SearchOutlined />
								</Button>
							</Col>
						</Row>}
					<Row className={styles.fullWidthRow}>
						<Col xs={{ span: 24 }} lg={{ span: 12 }} xl={{ span: 12, offset: 4 }} style={{ display: "flex" }} className={"mb-2"}>
							<Switch checked={!useGenericSearch} onChange={(e) => { dispatch(setGenericSearchEnabled(!e)) }} style={{ marginRight: 10 }} />
							{t('VehicleModel.GenericKeywordTitle')}
						</Col>
					</Row>
				</Form>
				<Row justify="center" align="middle">
					<Col xs={{ span: 24 }} className={styles.centerRow}>
						<Space>
							<Button size="large" onClick={() => { setShowCsvSearch(true) }}>
								{t('VehicleModel.CsvSearch')} <UploadOutlined />
							</Button>
							{feedbackUrl && <ResponsiveButton
								type="text"
								size="large"
								onClick={() => {
									window.open(feedbackUrl, "_blank")
								}}
								title={t("VehicleModel.ReportIssue")} icon={<ContainerOutlined />}
								showSmallerText={true}
							/>
							}
							
							<Can requiredPermission={[Permission.COMMON_EDIT]}>
								<ResponsiveButton size="large"
								  title={t("VehicleModel.AddVehicleModel")} icon={<PlusCircleOutlined />}
								  showSmallerText={true} type="text" onClick={() => { setNewVehicleModelModal(true) }}/>
							</Can>
						</Space>
					</Col>
				</Row>
			</Space>
			<VehicleCompatibilitySearchModal
				isModalOpen={showCsvSearch}
				onComplete={(searchedData, totalRecord, matchedRecord) => {
					setShowCsvSearch(false);
					history.push(RouteKey.VEHICLE_COMPATIBILITY, {
						records: searchedData.map(i => i.id)
					});

					dispatch(
						addToast({
							title: i18n.t("VehicleModel.CsvSearchSubmitted"),
							type: "success"
						})
					);
				}}
				groupedModel={true}
				onClose={() => { setShowCsvSearch(false) }} />
			<VehicleMakeModelYearFormModal
				isModalOpen={newVehicleModelModal}
				onClose={() => {
					setNewVehicleModelModal(false);
				}} />
		</Spin>
	)
}

export default VehicleCompatibilitySearch;